
import { computePosition, shift, flip, offset, size } from '@floating-ui/dom';

export default {
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      required: false,
      default: ''
    },

    align: {
      type: String,
      validator: value =>
        [
          'auto',
          'top',
          'right',
          'bottom',
          'left',
          'top-start',
          'top-middle',
          'top-end',
          'right-start',
          'right-middle',
          'right-end',
          'bottom-start',
          'bottom-middle',
          'bottom-end',
          'left-start',
          'left-middle',
          'left-end'
        ].includes(value),
      default: 'bottom'
    },

    autoSize: {
      type: Boolean,
      default: false
    },

    active: {
      type: Boolean
    },

    open: {
      type: Boolean,
      default: undefined
    },

    sheet: {
      type: [Boolean, String],
      default: false,
      validator: value => [false, true, 'bottom', 'top'].includes(value)
    },

    offset: {
      type: Number,
      default: 8
    },

    disabled: {
      type: Boolean,
      required: false
    },

    /**
     * @ignore
     */
    disableFocusStealing: {
      type: Boolean
    },

    appearance: {
      type: String,
      default: 'control',
      validator: value => ['control', 'minimal'].includes(value)
    }
  },

  data () {
    return {
      internalOpen: false,
      isSheet: false,
      location: {
        '--max-height': undefined
      }
    };
  },

  computed: {
    impliedAlignment () {
      if (this.align) {
        return this.align;
      }

      if (this.appearance === 'minimal') {
        return 'bottom-middle';
      }

      return 'bottom';
    },

    sheetPosition () {
      if (typeof this.sheet === 'string') { return this.sheet; }

      if (this.impliedAlignment.includes('top')) { return 'top'; }

      return 'bottom';
    },

    isOpen: {
      get () {
        if (this.open !== undefined) { return this.open; }

        return this.internalOpen;
      },

      async set (value) {
        if (this.open !== undefined) {
          if (this.open !== value) {
            this.$emit('update:open', value);
          }
        } else { this.internalOpen = value; }

        await this.$nextTick();
        this.alignDropdown();

        if (this.disableFocusStealing) { return; }

        if (value) {
          setTimeout(() => this.alignDropdown(), 50);
        }
      }
    }
  },

  watch: {
    align () {
      this.alignDropdown();
    },

    async open () {
      if (this.open) {
        await this.$nextTick();

        this.alignDropdown();
      }
    }
  },

  mounted () {
    this.alignDropdown();

    window.addEventListener(
      'scroll',
      () => requestAnimationFrame(() => {
        if (this.isOpen) { this.alignDropdown(); }
      }),
      { passive: true }
    );

    window.addEventListener('click', (e) => {
      const path = e.composedPath();

      if (!this.$el || this.$el === e.target || path.includes(this.$el)) { return; }

      this.isOpen = false;
    });

    window.addEventListener('blur', () => {
      if (this.isOpen) { this.isOpen = false; }
    });
  },

  methods: {
    submit (event) {
      this.$emit('submit', event);

      this.close();
    },

    click () {
      this.toggle();
    },

    async close (e) {
      this.isOpen = false;

      await this.$nextTick();
      await new Promise(resolve => setTimeout(resolve, 260));

      if (!e) {
        return;
      }

      const element = document.elementFromPoint(e.clientX, e.clientY);
      if (this.$el.contains(element)) { return; }

      element?.dispatchEvent(e);
    },

    async alignDropdown () {
      if (this.sheet && window.matchMedia('only screen and (max-width: 800px)').matches) {
        this.location = {
          ...this.location,
          transform: 'none',
          '--width': '100vw'
        };

        this.isSheet = true;
        return;
      }

      this.isSheet = false;

      const button = this.$el.firstElementChild;

      if (!this.$refs.dropdown) { return; }

      this.$refs.dropdown.style.setProperty(
        '--width',
        this.impliedAlignment.includes('middle') ? 'unset' : button.clientWidth + 'px'
      );

      const middleware = [shift({ padding: this.offset }), flip(), offset(this.offset)];

      if (this.autoSize) {
        middleware.push(
          size({
            apply: ({ height }) => {
              this.location['--max-height'] = height + 'px';
            },
            padding: {
              top: 80,
              left: 8,
              right: 8,
              bottom: 8
            }
          })
        );
      }

      const position = await computePosition(button, this.$refs.dropdown, {
        placement: this.impliedAlignment,
        strategy: 'fixed',
        middleware
      });

      let width = 0;

      if (!(this.impliedAlignment.includes('end') || this.impliedAlignment.includes('start') || this.impliedAlignment.includes('middle'))) { width = button.clientWidth + 'px'; }

      this.location = {
        ...this.location,
        transform: `translate(${Math.round(position.x)}px, ${Math.round(position.y)}px)`,
        '--width': width
      };
    },

    toggle () {
      if (this.disabled) { return; }
      this.isOpen = !this.isOpen;
    }
  }
};
